import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Users, DollarSign, Calendar, ArrowRightLeft } from 'lucide-react';
import {
  GlassModal,
  GlassButton,
  GlassBadge,
  GlassCard,
  GlassLoadingSpinner,
} from '@/design-system';
import { DataTable } from '@/components/ui';
import TransferResourceModal from '@/pages/resources/components/TransferResourceModal';
import { transferResource } from '@/store/slices/resourceTeamAssignmentsSlice';
import { fetchTeams } from '@/store/slices/teamsSlice';
import type { AppDispatch, RootState } from '@/store';
import type { Team, ResourceTeamAssignment, RateCard, Resource, Column } from '@/types';
import { resourceTeamAssignmentsApi, rateCardsApi } from '@/utils/api';

interface TeamRosterModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team | null;
}

interface RosterRow {
  id: string;
  assignment: ResourceTeamAssignment;
  resource: Resource;
  resourceName: string;
  resourceId: string;
  resourceType: string;
  resourceLevel: string;
  hourlyRate: number | null;
  annualCost: number | null;
  startDate: string;
}

const TeamRosterModal: React.FC<TeamRosterModalProps> = ({ isOpen, onClose, team }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => ({ user: state.auth?.user }));

  const [assignments, setAssignments] = useState<ResourceTeamAssignment[]>([]);
  const [rateCards, setRateCards] = useState<RateCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transferTarget, setTransferTarget] = useState<RosterRow | null>(null);

  const canTransfer = user && ['ADMIN', 'PMO', 'PROGRAM_LEAD', 'EXECUTIVE'].includes(user.role);

  useEffect(() => {
    if (isOpen && team) {
      loadRoster();
    }
  }, [isOpen, team]);

  const loadRoster = async () => {
    if (!team) return;
    setLoading(true);
    setError(null);

    try {
      const [assignRes, rateRes] = await Promise.all([
        resourceTeamAssignmentsApi.getAll({ teamId: team.id, active: true }),
        rateCardsApi.getByYear(new Date().getFullYear()),
      ]);

      if (assignRes.success) {
        setAssignments(
          (assignRes.data as { assignments: ResourceTeamAssignment[] })?.assignments || []
        );
      }
      if (rateRes.success) {
        setRateCards((rateRes.data as { rateCards: RateCard[] })?.rateCards || []);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load roster');
    } finally {
      setLoading(false);
    }
  };

  if (!team) return null;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const rosterData: RosterRow[] = assignments
    .filter((a) => a.resource)
    .map((a) => {
      const resource = a.resource!;
      const rateCard = rateCards.find((rc) => rc.resourceLevelId === resource.resourceLevelId);
      const hourlyRate = rateCard?.hourlyRate ?? null;
      const annualCost = hourlyRate ? hourlyRate * 40 * 52 : null;

      return {
        id: a.id,
        assignment: a,
        resource,
        resourceName: `${resource.firstName} ${resource.lastName}`,
        resourceId: resource.resourceId,
        resourceType: resource.resourceType,
        resourceLevel: resource.resourceLevel?.code || 'Unknown',
        hourlyRate,
        annualCost,
        startDate: a.startDate,
      };
    });

  const totalActualCost = rosterData.reduce((sum, r) => sum + (r.annualCost || 0), 0);

  const handleTransfer = async (data: { newTeamId: string; transferDate?: string }) => {
    if (!transferTarget) return;
    const result = await dispatch(
      transferResource({
        resourceId: transferTarget.resource.id,
        newTeamId: data.newTeamId,
        transferDate: data.transferDate || new Date().toISOString().split('T')[0],
      })
    );
    if (transferResource.fulfilled.match(result)) {
      setTransferTarget(null);
      await loadRoster();
      dispatch(fetchTeams(undefined));
    }
  };

  const columns: Column<RosterRow>[] = [
    {
      key: 'resourceName',
      header: 'Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-foreground text-sm">{value as string}</div>
          <div className="text-xs text-muted-foreground font-mono">{row.resourceId}</div>
        </div>
      ),
    },
    {
      key: 'resourceType',
      header: 'Type',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-foreground">{(value as string).replace(/_/g, ' ')}</span>
      ),
    },
    {
      key: 'resourceLevel',
      header: 'Level',
      sortable: true,
      render: (value) => (
        <GlassBadge variant="info" size="sm">
          {value as string}
        </GlassBadge>
      ),
    },
    {
      key: 'hourlyRate',
      header: 'Rate/hr',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-foreground">{value ? `$${value}` : 'N/A'}</span>
      ),
    },
    {
      key: 'annualCost',
      header: 'Annual Cost',
      sortable: true,
      render: (value) => (
        <span className="text-sm font-medium text-foreground">
          {value ? formatCurrency(value as number) : 'N/A'}
        </span>
      ),
    },
    {
      key: 'startDate',
      header: 'Start Date',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-1 text-sm text-foreground">
          <Calendar className="w-3.5 h-3.5" />
          {formatDate(value as string)}
        </div>
      ),
    },
  ];

  const tableActions = canTransfer
    ? [
        {
          label: 'Transfer',
          icon: ArrowRightLeft,
          onClick: (row: RosterRow) => setTransferTarget(row),
        },
      ]
    : [];

  return (
    <>
      <GlassModal
        isOpen={isOpen && !transferTarget}
        onClose={onClose}
        title={`${team.name} - Roster`}
        subtitle={`${team.program?.name || 'No Program'}`}
        icon={<Users className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="xl"
      >
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <GlassLoadingSpinner size="lg" variant="gradient" label="Loading roster..." />
          </div>
        ) : error ? (
          <div className="bg-status-error/10 border border-status-error/30 text-status-error px-4 py-3 rounded-lg">
            {error}
          </div>
        ) : (
          <div className="space-y-4">
            {rosterData.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-foreground">No team members assigned</p>
              </div>
            ) : (
              <div className="rounded-xl overflow-hidden border border-[var(--border)] bg-white/40 dark:bg-white/5 backdrop-blur-sm shadow-sm">
                <DataTable
                  data={rosterData}
                  columns={columns}
                  actions={tableActions}
                  searchable
                  searchPlaceholder="Search members..."
                  sortable
                  pagination
                  pageSize={10}
                />
              </div>
            )}

            {rosterData.length > 0 && (
              <GlassCard variant="default" padding="md" className="flex items-center justify-between">
                <div className="font-semibold text-foreground flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Total Actual Annual Cost
                </div>
                <div className="text-xl font-bold text-accent-blue">
                  {formatCurrency(totalActualCost)}
                </div>
              </GlassCard>
            )}
          </div>
        )}
      </GlassModal>

      {/* Transfer Modal */}
      {transferTarget && (
        <GlassModal
          isOpen={!!transferTarget}
          onClose={() => setTransferTarget(null)}
          title="Transfer Resource"
          subtitle={`${transferTarget.resourceName}`}
          icon={<ArrowRightLeft className="w-5 h-5" />}
          iconGradient="amber"
          size="lg"
        >
          <TransferResourceModal
            resource={transferTarget.resource}
            currentAssignment={transferTarget.assignment}
            onTransfer={handleTransfer}
            onClose={() => setTransferTarget(null)}
          />
        </GlassModal>
      )}
    </>
  );
};

export default TeamRosterModal;
